import React, { useState, useEffect, useRef } from 'react';
import MultiSelectDropdown from '../../components/ui/MultiSelectDropdown.jsx';
import { PREDEFINED_CITIES } from '../../utils/constants';

const PREDEFINED_LOCATIONS = PREDEFINED_CITIES;

const AssetClasses = ({
    formData,
    handleInputChange,
    locationInput,
    setLocationInput,
    handleAddLocation,
    handleRemoveLocation
}) => {
    const [filteredLocations, setFilteredLocations] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [limitError, setLimitError] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (locationInput) {
            const filtered = PREDEFINED_LOCATIONS.filter(loc =>
                loc.toLowerCase().includes(locationInput.toLowerCase()) &&
                !formData.preferred_locations?.includes(loc)
            );
            setFilteredLocations(filtered);
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    }, [locationInput, formData.preferred_locations]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    const handleSelectLocation = (location) => {
        const currentLocations = formData.preferred_locations || [];
        if (!currentLocations.includes(location)) {
            handleInputChange({
                target: {
                    name: 'preferred_locations',
                    value: [...currentLocations, location]
                }
            });
        }
        setLocationInput('');
        setShowSuggestions(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const val = locationInput.trim();
            if (!val) return;

            setLimitError('');

            // Check if matches any predefined (case-insensitive)
            const match = PREDEFINED_LOCATIONS.find(
                loc => loc.toLowerCase() === val.toLowerCase()
            );

            const locationToAdd = match || val;
            const currentLocations = formData.preferred_locations || [];

            if (currentLocations.includes(locationToAdd)) {
                setLocationInput('');
                setShowSuggestions(false);
                return;
            }

            // If it's custom (no match), check limit
            if (!match) {
                // Count existing custom locations
                const customCount = currentLocations.filter(
                    loc => !PREDEFINED_LOCATIONS.includes(loc)
                ).length;

                if (customCount >= 5) {
                    setLimitError('You can only add custom locations up to 5 max');
                    return;
                }
            }

            // Add
            handleInputChange({
                target: {
                    name: 'preferred_locations',
                    value: [...currentLocations, locationToAdd]
                }
            });
            setLocationInput('');
            setShowSuggestions(false);
        }
    };

    return (
        <div className="form-section">
            <h3 className="section-heading">Asset Classes for Investment Options (Choose as Applicable)</h3>
            <div className="row">
                <div className="col-12 col-md-6">
                    <div className="form-group">
                        <label className="form-label">Real Estate:</label>
                        <MultiSelectDropdown
                            name="real_estate"
                            options={["Land", "Shop", "Office", "Residential", "Other"]}
                            selected={formData.real_estate}
                            onChange={handleInputChange}
                            placeholder="Select Real Estate Type"
                        />
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className="form-group">
                        <label className="form-label">Running Business:</label>
                        <MultiSelectDropdown
                            name="running_business"
                            options={["Shops", "Factories", "Offices", "Companies - Listed/Unlisted"]}
                            selected={formData.running_business}
                            onChange={handleInputChange}
                            placeholder="Select Running Business Type"
                        />
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className="form-group">
                        <label className="form-label">Distressed Assets:</label>
                        <MultiSelectDropdown
                            name="distresses_assets"
                            options={["NRRAs", "Unlisted/Pre-List Shares"]}
                            selected={formData.distresses_assets}
                            onChange={handleInputChange}
                            placeholder="Select Distressed Assets Type"
                        />
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className="form-group">
                        <label className="form-label">Other Asset Types:</label>
                        <MultiSelectDropdown
                            name="other_asset_types"
                            options={["Shell Companies", "Loss Making Companies", "Companies", "NBFCs", "ARTs", "Any Other"]}
                            selected={formData.other_asset_types}
                            onChange={handleInputChange}
                            placeholder="Select Other Asset Type"
                        />
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className="form-group" ref={wrapperRef} style={{ position: 'relative' }}>
                        <label className="form-label">Preferred Location(s)</label>
                        <input
                            type="text"
                            value={locationInput}
                            onChange={(e) => {
                                setLocationInput(e.target.value);
                                if (limitError) setLimitError('');
                            }}
                            onKeyDown={handleKeyDown}
                            onFocus={() => locationInput && setShowSuggestions(true)}
                            placeholder="Type to search or enter to add custom location"
                            className="form-input"
                            autoComplete="off"
                        />
                        {showSuggestions && filteredLocations.length > 0 && (
                            <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                {filteredLocations.map((item, index) => (
                                    <li
                                        key={index}
                                        className="list-group-item list-group-item-action cursor-pointer"
                                        onClick={() => handleSelectLocation(item)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="selected-locations mt-2 d-flex flex-wrap gap-2">
                            {formData.preferred_locations.map((loc, index) => (
                                <span key={index} className="badge bg-light text-dark border p-2 d-flex align-items-center">
                                    {loc}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveLocation(index)}
                                        className="btn-close ms-2"
                                        style={{ fontSize: '0.5rem' }}
                                        aria-label="Close"
                                    ></button>
                                </span>
                            ))}
                        </div>

                        {limitError && <div className="text-danger mt-1" style={{ fontSize: '0.85rem' }}>{limitError}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssetClasses;
